package com.schoolconnect.app.feature.splash

import androidx.compose.animation.core.RepeatMode
import androidx.compose.animation.core.animateFloat
import androidx.compose.animation.core.infiniteRepeatable
import androidx.compose.animation.core.rememberInfiniteTransition
import androidx.compose.animation.core.tween
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.widthIn
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.alpha
import androidx.compose.ui.draw.scale
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.brand_header_proper
import ischoolconnect.sharedui.generated.resources.hero_scene_without_header
import ischoolconnect.sharedui.generated.resources.ischoolconnect_splash_logo
import ischoolconnect.sharedui.generated.resources.trust_footer_bar
import org.jetbrains.compose.resources.painterResource
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.width

@Composable
fun SplashScreen(
    state: SplashUiState,
    modifier: Modifier = Modifier
) {
    val infinite = rememberInfiniteTransition(label = "splash")
    val logoPulse by infinite.animateFloat(
        initialValue = 0.94f,
        targetValue = 1.06f,
        animationSpec = infiniteRepeatable(animation = tween(2200), repeatMode = RepeatMode.Reverse),
        label = "logoPulse"
    )

    BoxWithConstraints(
        modifier = modifier
            .fillMaxSize()
            .background(
                Brush.linearGradient(
                    colors = listOf(
                        Color.White,
                        Color(0xFFF5F8FF),
                        Color(0xFFEDF4FF)
                    )
                )
            )
        ) {
            val compact = maxWidth < 720.dp
            val wide = maxWidth >= 1100.dp

            SplashGlow(
                modifier = Modifier
                    .align(Alignment.TopStart)
                    .padding(top = if (compact) 44.dp else 72.dp, start = if (compact) 20.dp else 56.dp)
                    .size(if (compact) 140.dp else 220.dp),
                tint = SplashMid.copy(alpha = 0.20f)
            )
            SplashGlow(
                modifier = Modifier
                    .align(Alignment.BottomEnd)
                    .padding(bottom = if (compact) 54.dp else 78.dp, end = if (compact) 14.dp else 52.dp)
                    .size(if (compact) 180.dp else 280.dp),
                tint = SplashAccent.copy(alpha = 0.18f)
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .padding(horizontal = if (compact) 20.dp else 32.dp, vertical = if (compact) 24.dp else 32.dp),
                horizontalAlignment = Alignment.CenterHorizontally,
                verticalArrangement = Arrangement.Center
            ) {
                Column(
                    modifier = Modifier
                        .widthIn(max = if (compact) 380.dp else 780.dp)
                        .fillMaxWidth(),
                    horizontalAlignment = Alignment.CenterHorizontally,
                    verticalArrangement = Arrangement.spacedBy(if (compact) 18.dp else 22.dp)
                ) {
                    Image(
                        painter = painterResource(Res.drawable.brand_header_proper),
                        contentDescription = state.appName,
                        modifier = Modifier
                            .widthIn(max = if (compact) 300.dp else 460.dp)
                            .fillMaxWidth()
                            .height(if (compact) 84.dp else 108.dp)
                            .alpha(0.99f),
                        contentScale = ContentScale.Fit
                    )

                    Text(
                        text = state.headline,
                        style = if (compact) MaterialTheme.typography.headlineSmall else MaterialTheme.typography.headlineMedium,
                        fontWeight = FontWeight.Bold,
                        color = Color(0xFF1B2C67)
                    )
                    Text(
                        text = state.subtitle,
                        style = MaterialTheme.typography.bodyLarge,
                        color = Color(0xFF63729A)
                    )

                    Box(
                        modifier = Modifier.fillMaxWidth(),
                        contentAlignment = Alignment.Center
                    ) {
                        Box(
                            modifier = Modifier
                                .size(if (compact) 250.dp else 320.dp)
                                .scale(logoPulse)
                                .background(
                                    Brush.radialGradient(
                                        colors = listOf(
                                            Color(0xFF3C7DFF).copy(alpha = 0.10f),
                                            Color(0xFFBFD6FF).copy(alpha = 0.06f),
                                            Color.Transparent
                                        )
                                    )
                                ),
                            contentAlignment = Alignment.Center
                        ) {
                            Image(
                                painter = painterResource(Res.drawable.ischoolconnect_splash_logo),
                                contentDescription = null,
                                modifier = Modifier
                                    .fillMaxSize()
                                    .padding(if (compact) 14.dp else 16.dp),
                                contentScale = ContentScale.Fit
                            )
                        }
                    }

                    Image(
                        painter = painterResource(Res.drawable.hero_scene_without_header),
                        contentDescription = null,
                        modifier = Modifier
                            .fillMaxWidth()
                            .widthIn(max = if (wide) 520.dp else 420.dp)
                            .alpha(0.96f),
                        contentScale = ContentScale.Fit
                    )

                    Image(
                        painter = painterResource(Res.drawable.trust_footer_bar),
                        contentDescription = null,
                        modifier = Modifier
                            .fillMaxWidth()
                            .widthIn(max = if (compact) 340.dp else 520.dp)
                            .alpha(0.96f),
                        contentScale = ContentScale.FillWidth
                    )

                    Text(
                        text = state.status,
                        style = MaterialTheme.typography.labelLarge,
                        color = Color(0xFF6B7AA5)
                    )
                }

                Spacer(modifier = Modifier.height(if (compact) 14.dp else 18.dp))

                Row(
                    horizontalArrangement = Arrangement.Center
                ) {
                    SplashTrustChip(text = "Secure session")
                    Spacer(modifier = Modifier.width(10.dp))
                    SplashTrustChip(text = "Fast sync")
                    Spacer(modifier = Modifier.width(10.dp))
                    SplashTrustChip(text = "Login ready")
                }
            }
        }
    }
